import { useState, useEffect } from 'react';
import { MessageSquare, Mail, Building, Users, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge, statusBadgeVariants } from '../../components/ui/Badge';
import { Modal, ModalActions } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { getInquiries, updateInquiryStatus } from '../../lib/supabase/queries';
import type { Inquiry, InquiryStatus } from '../../types';

const statusOptions = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'closed', label: 'Closed' },
];

export function InquiriesPage() {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function fetchInquiries() {
      try {
        const data = await getInquiries();
        setInquiries(data);
      } catch (error) {
        console.error('Error fetching inquiries:', error);
      }
    }

    fetchInquiries();
  }, []);

  const handleViewInquiry = (inquiry: Inquiry) => {
    setSelectedInquiry(inquiry);
    setShowDetailModal(true);
  };

  const handleUpdateStatus = async (newStatus: InquiryStatus) => {
    if (!selectedInquiry) return;

    setIsSaving(true);
    try {
      const success = await updateInquiryStatus(selectedInquiry.id, newStatus);
      if (success) {
        setInquiries(
          inquiries.map((i) =>
            i.id === selectedInquiry.id ? { ...i, status: newStatus } : i
          )
        );
        setSelectedInquiry({ ...selectedInquiry, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate stats
  const newCount = inquiries.filter((i) => i.status === 'new').length;
  const contactedCount = inquiries.filter((i) => i.status === 'contacted').length;
  const convertedCount = inquiries.filter((i) => i.status === 'converted').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-obsidian dark:text-signal">
          Inquiries
        </h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          Manage contact form submissions
        </p>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-4 gap-4">
        <Card variant="bordered" padding="md">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Total</p>
                <p className="text-2xl font-bold text-obsidian dark:text-signal mt-1">
                  {inquiries.length}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card variant="bordered" padding="md">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">New</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                  {newCount}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card variant="bordered" padding="md">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Contacted
                </p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">
                  {contactedCount}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <Mail className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card variant="bordered" padding="md">
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Converted
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  {convertedCount}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inquiries List */}
      <Card variant="bordered" padding="none">
        <CardHeader className="p-6 border-b border-slate-200 dark:border-slate-700">
          <CardTitle>All Inquiries</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {inquiries.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No inquiries yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {inquiries.map((inquiry) => (
                <div
                  key={inquiry.id}
                  className="p-4 hover:bg-slate-50 dark:hover:bg-obsidian-100 cursor-pointer"
                  onClick={() => handleViewInquiry(inquiry)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <p className="font-semibold text-obsidian dark:text-signal">
                          {inquiry.name}
                        </p>
                        <Badge
                          variant={statusBadgeVariants[inquiry.status] || 'default'}
                        >
                          {inquiry.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                        <span className="flex items-center gap-1">
                          <Building className="w-4 h-4" />
                          {inquiry.company}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {inquiry.email}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                        {inquiry.message}
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {format(new Date(inquiry.created_at), 'MMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inquiry Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Inquiry Details"
        size="lg"
      >
        {selectedInquiry && (
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Name
                </label>
                <p className="text-obsidian dark:text-signal">
                  {selectedInquiry.name}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Email
                </label>
                <a
                  href={`mailto:${selectedInquiry.email}`}
                  className="text-amber hover:underline"
                >
                  {selectedInquiry.email}
                </a>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Company
                </label>
                <p className="text-obsidian dark:text-signal">
                  {selectedInquiry.company}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Company Size
                </label>
                <p className="text-obsidian dark:text-signal">
                  {selectedInquiry.company_size}
                </p>
              </div>
              {selectedInquiry.churn_rate && (
                <div>
                  <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Churn Rate
                  </label>
                  <p className="text-obsidian dark:text-signal">
                    {selectedInquiry.churn_rate}
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Submitted
                </label>
                <p className="text-obsidian dark:text-signal">
                  {format(
                    new Date(selectedInquiry.created_at),
                    'MMM d, yyyy h:mm a'
                  )}
                </p>
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                Message
              </label>
              <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {selectedInquiry.message}
                </p>
              </div>
            </div>

            {/* Status Update */}
            <div>
              <Select
                label="Update Status"
                value={selectedInquiry.status}
                onChange={(e) =>
                  handleUpdateStatus(e.target.value as InquiryStatus)
                }
                options={statusOptions}
              />
            </div>
          </div>
        )}
        <ModalActions>
          <a
            href={`mailto:${selectedInquiry?.email}?subject=Re: Your ChurnLogix Inquiry`}
            className="mr-auto"
          >
            <Button variant="outline" leftIcon={<Mail className="w-4 h-4" />}>
              Send Email
            </Button>
          </a>
          <Button variant="outline" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </ModalActions>
      </Modal>
    </div>
  );
}

export default InquiriesPage;
